import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import type {
    DokumentAbschnitt,
    DokumentBlock,
    DokumentModell,
    TabellenSpalte,
} from '../../components/planungsphase/dokumentation/planungDokumentModell';
import { gliederung } from '../../components/planungsphase/dokumentation/planungDokumentModell';
import type { Anschluss, PdfKontext } from '../../components/planungsphase/planungIntegration';
import { buildCompanyForPdf } from './companyForPdf';
import {
    A4_GEOMETRY,
    type CompanyInfo,
    createLetterhead,
    drawPage1Header,
    FOOTER_RESERVE,
    footerColumnWidths,
    type LetterheadContext,
} from './letterhead';
import { sichereTextausgabe } from './winAnsi';

/**
 * Dokumentation der Planungsphase als PDF — der Renderer.
 *
 * Diese Datei trifft KEINE fachliche Entscheidung. Was im Dokument steht,
 * entscheidet `baueDokumentModell` (`planungDokumentModell.ts`); hier wird
 * nur gesetzt. Muster: `renderBauordnungPdf.ts`.
 *
 * ─── Gestaltung ───────────────────────────────────────────────────────────
 * Das Dokument soll wie ein Gutachten aus dem Baugewerbe aussehen, nicht wie
 * ein Bildschirmausdruck (Betreiber 31.10.2026: „ohne gefärbte Kästen, wo
 * Text drin steht"). Deshalb:
 *   - eine Schriftfamilie (Helvetica wie alle Geschaeftsbriefe), Hierarchie
 *     nur ueber Groesse und Schnitt, Text fast schwarz;
 *   - Tabellen mit Linien statt Flaechen: kraeftige Linie ueber und unter dem
 *     Kopf, Haarlinien zwischen den Zeilen, kraeftige Linie am Tabellenende.
 *     KEINE hinterlegte Zelle, kein Kasten, keine Ampelfarbe;
 *   - Briefkopf und Fusszeile aus `letterhead.ts` (Firmendaten aus dem
 *     `PdfKontext`), Kopfzeile ab Seite 2 mit Bauvorhaben und Titel,
 *     Fusszeile mit Stand und „Seite x von y".
 *
 * ─── Inhaltsverzeichnis ───────────────────────────────────────────────────
 * Die Zahl der Eintraege steht vor dem Satz fest (`gliederung`). Das
 * Verzeichnis bekommt deshalb VORAB so viele Seiten, wie es braucht; der
 * Inhalt wird danach gesetzt, die Seite jeder Ueberschrift notiert und das
 * Verzeichnis zum Schluss auf die freigehaltenen Seiten geschrieben. Anders
 * als beim nachtraeglichen Einschieben (`renderBauordnungPdf.ts`) muss keine
 * Seitenzahl verschoben werden, und das Briefpapier liegt auch auf den
 * Verzeichnisseiten.
 *
 * ─── Umbruch ──────────────────────────────────────────────────────────────
 * Eine Ueberschrift steht nie allein am Seitenende: sie nimmt mindestens den
 * Anfang des folgenden Blocks mit. Absaetze lassen keine einzelne Zeile
 * zurueck oder vorauslaufen. Tabellen wiederholen ihren Kopf auf jeder
 * Folgeseite, eine Zeile wird nur getrennt, wenn sie hoeher ist als eine
 * Seite. Ueberlange Woerter in schmalen Spalten werden mit Trennstrich
 * umbrochen statt mitten im Wort abgeschnitten.
 */

// ═══════════════════════════════════════════════════════════════════════════
// SATZ
// ═══════════════════════════════════════════════════════════════════════════

const GEO = A4_GEOMETRY;
const PT = 0.3528; // mm je Punkt

type Rgb = [number, number, number];

/** Farben. Nur Schwarz und Grau — Weiss ist der Papierton. */
export const FARBE: Readonly<Record<'text' | 'grau' | 'linie' | 'haar' | 'papier', Rgb>> = {
    text: [20, 20, 20],
    grau: [90, 90, 90],
    linie: [35, 35, 35],
    haar: [160, 160, 160],
    papier: [255, 255, 255],
};

const SCHRIFT = {
    titel: 20,
    untertitel: 11.5,
    h1: 12.5,
    h2: 10.5,
    text: 10,
    tabelle: 8,
    klein: 8,
    verzeichnisTitel: 12.5,
};

const ZEILE = {
    titelei: 6.2,
    text: 5.0,
    angabe: 5.0,
    h1: 6.4,
    h2: 5.4,
    verzeichnis1: 6.2,
    verzeichnis2: 5.3,
};

const ABSTAND = {
    absatz: 2.6,
    block: 3.5,
    vorH1: 7,
    nachH1: 2.6,
    vorH2: 4.5,
    nachH2: 1.6,
};

/** Tabulator fuer die Ueberschriftentexte hinter der Nummer, in mm. */
const TAB = 11;

/** Breite der Bezeichnungsspalte einer Angabenliste, in mm. */
const ANGABE_BEZEICHNUNG = 64;

const TABELLE_POLSTER = { oben: 1.4, unten: 1.4, seite: 1.2 };

/** Ein kurzer Einleitungsabsatz (bis zu so vielen Zeilen) bleibt mit der
 *  folgenden Tabelle auf einer Seite. */
const EINLEITUNG_MAX_ZEILEN = 6;
const LINIE = { kraeftig: 0.3, kopf: 0.2, haar: 0.1 };

/** Zeilenhoehe, die autoTable bei der Tabellenschrift setzt (Faktor 1,15). */
const TABELLE_ZEILE = SCHRIFT.tabelle * 1.15 * PT;

/** Grundlinie einer Textzeile, deren Zeilenkasten bei `oben` beginnt. */
const grundlinie = (oben: number, groesse: number, zeilenhoehe: number): number => {
    const mm = groesse * PT;
    return oben + (zeilenhoehe - mm) / 2 + 0.78 * mm;
};

// ═══════════════════════════════════════════════════════════════════════════
// REINE HELFER (exportiert fuer Tests)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Spaltenbreiten in mm: feste Breiten aus dem Modell, der Rest nach Gewicht
 * (Vorgabe 1) auf die Spalten ohne Breite. Passen die festen Breiten nicht in
 * den Satzspiegel, werden ALLE Spalten im Verhaeltnis gestaucht — die Tabelle
 * laeuft nie ueber den Rand.
 */
export const spaltenBreiten = (spalten: readonly TabellenSpalte[], gesamt: number = GEO.contentWidth): number[] => {
    const n = spalten.length;
    if (!n) return [];
    const hatBreite = (sp: TabellenSpalte) => typeof sp.breite === 'number' && sp.breite > 0;
    const gewicht = (sp: TabellenSpalte) => (typeof sp.gewicht === 'number' && sp.gewicht > 0 ? sp.gewicht : 1);
    const fest = spalten.reduce((s, sp) => s + (hatBreite(sp) ? (sp.breite as number) : 0), 0);
    const freie = spalten.filter(sp => !hatBreite(sp));
    const frei = freie.length;
    const gewichte = freie.reduce((s, sp) => s + gewicht(sp), 0);
    const rest = gesamt - fest;
    const mindest = 18;
    if (frei > 0 && rest >= frei * mindest) {
        return spalten.map(sp => (hatBreite(sp) ? (sp.breite as number) : (rest * gewicht(sp)) / gewichte));
    }
    if (frei === 0 && fest <= gesamt) {
        // Alles fest und Platz uebrig: die letzte Spalte nimmt ihn.
        return spalten.map((sp, i) => (i === n - 1 ? (sp.breite as number) + rest : (sp.breite as number)));
    }
    const roh = spalten.map(sp => (typeof sp.breite === 'number' && sp.breite > 0 ? sp.breite : mindest));
    const summe = roh.reduce((s, b) => s + b, 0);
    return roh.map(b => (b * gesamt) / summe);
};

const VOKAL = /[aeiouäöüyAEIOUÄÖÜY]/;

/** Zerlegt EIN Wort, das breiter ist als `max`, in Stuecke mit Trennstrich.
 *  Bevorzugt wird ein Schnitt vor Konsonant + Vokal („Erschütterungs-
 *  beweis-sicherung"), nie hinter „c" (ch, ck, sch bleiben zusammen). */
const zerhacke = (wort: string, max: number, messen: (t: string) => number): string[] => {
    const stuecke: string[] = [];
    let rest = wort;
    while (rest && messen(rest) > max) {
        let passt = 0;
        for (let k = 1; k < rest.length; k++) {
            if (messen(`${rest.slice(0, k)}-`) <= max) passt = k; else break;
        }
        if (passt < 1) passt = 1;
        let schnitt = passt;
        for (let k = passt; k >= 3; k--) {
            const a = rest[k - 1];
            const b = rest[k];
            const c = rest[k + 1] || '';
            if (rest.length - k < 3) continue;
            if (/[cC]/.test(a)) continue;
            if (!VOKAL.test(b) && VOKAL.test(c)) { schnitt = k; break; }
        }
        stuecke.push(`${rest.slice(0, schnitt)}-`);
        rest = rest.slice(schnitt);
    }
    if (rest) stuecke.push(rest);
    return stuecke;
};

/**
 * Bricht in einer Zeile jedes Wort um, das breiter ist als `max` — zuerst an
 * vorhandenen Binde- und Schraegstrichen, erst dann mit Trennstrich. Die
 * Bruchstellen werden als Zeilenumbruch eingesetzt; den uebrigen Umbruch
 * macht autoTable wie gewohnt.
 */
export const trenneUeberlangeWoerter = (zeile: string, max: number, messen: (t: string) => number): string => {
    if (!zeile || !(max > 0)) return zeile;
    return zeile.split(' ').map(wort => {
        if (!wort || messen(wort) <= max) return wort;
        // Datum, Zahl, Code: kein Trennstrich — „15.09.202-6" liest sich als
        // anderes Datum. Passt es nicht, bricht autoTable es wie bisher.
        if (!/[A-Za-zÄÖÜäöüß]{4,}/.test(wort)) return wort;
        const teile: string[] = [];
        let laufend = '';
        for (const z of wort) {
            laufend += z;
            if (z === '-' || z === '/') { teile.push(laufend); laufend = ''; }
        }
        if (laufend) teile.push(laufend);
        const zeilen: string[] = [];
        let aktuell = '';
        teile.forEach(teil => {
            if (messen(aktuell + teil) <= max) { aktuell += teil; return; }
            if (aktuell) zeilen.push(aktuell);
            if (messen(teil) <= max) { aktuell = teil; return; }
            const stuecke = zerhacke(teil, max, messen);
            zeilen.push(...stuecke.slice(0, -1));
            aktuell = stuecke[stuecke.length - 1] || '';
        });
        if (aktuell) zeilen.push(aktuell);
        return zeilen.join('\n');
    }).join(' ');
};

/** Dateiname — ohne Sonderzeichen und Leerzeichen, wie die Klaerungsliste.
 *  Die Fassung mit interner Bewertung traegt `_intern` am Ende: schon in der
 *  Ablage oder im Anhang einer Mail soll sie erkennbar sein. */
export const planungsdokumentationDateiname = (
    projektName: string,
    heuteIso: string,
    optionen: { intern?: boolean } = {},
): string => {
    const sauber = String(projektName || '')
        .replace(/[^\wÄÖÜäöüß\- ]+/g, '')
        .trim()
        .replace(/\s+/g, '_')
        .slice(0, 60);
    const tag = String(heuteIso || '').slice(0, 10) || 'ohne-Datum';
    const zusatz = optionen.intern ? 'intern' : '';
    return `${['Planungsdokumentation', sauber, tag, zusatz].filter(Boolean).join('_')}.pdf`;
};

/** Fusszeile jeder Seite der internen Fassung — schlichter Text, keine Farbe. */
export const FUSS_INTERN = 'Interne Fassung mit Bewertung des Koordinators, nicht zur Weitergabe';

// ═══════════════════════════════════════════════════════════════════════════
// DER SATZ
// ═══════════════════════════════════════════════════════════════════════════

interface SatzZustand {
    doc: jsPDF;
    lh: LetterheadContext;
    company: CompanyInfo;
    y: number;
    /** Oberkante des Inhalts auf Folgeseiten. */
    oben: number;
    /** Unterkante des Inhalts (Fusszeilenstreifen bleibt frei). */
    unten: number;
    fussReserve: number;
    /** Gliederungsnummer → Seite der Ueberschrift. */
    seiten: Map<string, number>;
}

const aktuelleSeite = (doc: jsPDF): number =>
    (doc.internal as unknown as { getCurrentPageInfo: () => { pageNumber: number } }).getCurrentPageInfo().pageNumber;

const farbe = (doc: jsPDF, f: Rgb) => doc.setTextColor(f[0], f[1], f[2]);
const linienfarbe = (doc: jsPDF, f: Rgb) => doc.setDrawColor(f[0], f[1], f[2]);

const schrift = (doc: jsPDF, stil: 'normal' | 'bold' | 'italic', groesse: number) => {
    doc.setFont('helvetica', stil);
    doc.setFontSize(groesse);
};

const umbrechen = (doc: jsPDF, text: string, breite: number): string[] =>
    doc.splitTextToSize(String(text || ''), breite) as string[];

const neueSeite = (s: SatzZustand) => {
    s.lh.addPage();
    s.y = s.oben;
};

const amSeitenkopf = (s: SatzZustand): boolean => s.y <= s.oben + 0.01;

// ── Hoehen fuer den Umbruch ───────────────────────────────────────────────

const ueberschriftZeilen = (s: SatzZustand, a: DokumentAbschnitt, ebene: number): string[] => {
    schrift(s.doc, 'bold', ebene === 1 ? SCHRIFT.h1 : SCHRIFT.h2);
    return umbrechen(s.doc, a.titel, GEO.contentWidth - TAB);
};

const ueberschriftHoehe = (s: SatzZustand, a: DokumentAbschnitt, ebene: number, mitVorlauf: boolean): number => {
    const zeilen = ueberschriftZeilen(s, a, ebene).length;
    const h1 = ebene === 1;
    return (mitVorlauf ? (h1 ? ABSTAND.vorH1 : ABSTAND.vorH2) : 0)
        + zeilen * (h1 ? ZEILE.h1 : ZEILE.h2)
        + (h1 ? ABSTAND.nachH1 : ABSTAND.nachH2);
};

const angabeZeilen = (s: SatzZustand, bezeichnung: string, wert: string): { links: string[]; rechts: string[] } => {
    schrift(s.doc, 'normal', SCHRIFT.text);
    return {
        links: umbrechen(s.doc, bezeichnung, ANGABE_BEZEICHNUNG - 3),
        rechts: umbrechen(s.doc, wert, GEO.contentWidth - ANGABE_BEZEICHNUNG),
    };
};

/** Eine kurze Angabenliste (bis acht Zeilen) wird nicht getrennt. */
const ANGABEN_ZUSAMMEN = 8;
const ANGABE_ABSTAND = 0.8;

const angabenHoehe = (s: SatzZustand, zeilen: readonly { bezeichnung: string; wert: string }[]): number =>
    zeilen.reduce((h, z) => {
        const { links, rechts } = angabeZeilen(s, z.bezeichnung, z.wert);
        return h + Math.max(links.length, rechts.length) * ZEILE.angabe + ANGABE_ABSTAND;
    }, 0);

const absatzZeilen = (s: SatzZustand, text: string): string[] => {
    schrift(s.doc, 'normal', SCHRIFT.text);
    return umbrechen(s.doc, text, GEO.contentWidth);
};

/** Hoehe von Tabellenkopf und erster Zeile — darunter beginnt keine Tabelle. */
const tabellenAnfangHoehe = (s: SatzZustand, block: Extract<DokumentBlock, { art: 'tabelle' }>): number => {
    const breiten = spaltenBreiten(block.spalten);
    const hoeheZeile = (zellen: string[], stil: 'normal' | 'bold'): number => {
        schrift(s.doc, stil, SCHRIFT.tabelle);
        const zeilen = zellen.reduce((max, z, i) => {
            const verfuegbar = Math.max(4, (breiten[i] || 20) - 2 * TABELLE_POLSTER.seite);
            return Math.max(max, umbrechen(s.doc, z, verfuegbar).length);
        }, 1);
        return zeilen * TABELLE_ZEILE + TABELLE_POLSTER.oben + TABELLE_POLSTER.unten;
    };
    const kopf = hoeheZeile(block.spalten.map(sp => sp.titel), 'bold');
    const erste = block.zeilen.length ? hoeheZeile(block.zeilen[0], 'normal') : 0;
    // Eine sehr hohe erste Zeile wird ohnehin umbrochen; dann genuegt ein Anfang.
    return kopf + Math.min(erste, 40) + 1;
};

const ZEICHNUNG_HOEHE = 34;

const blockAnfangHoehe = (s: SatzZustand, block: DokumentBlock): number => {
    switch (block.art) {
        case 'absatz':
            return Math.min(absatzZeilen(s, block.text).length, 2) * ZEILE.text;
        case 'angaben':
            return angabenHoehe(s, block.zeilen.length <= ANGABEN_ZUSAMMEN ? block.zeilen : block.zeilen.slice(0, 1));
        case 'tabelle':
            return tabellenAnfangHoehe(s, block);
        case 'zeichnung':
            return ZEICHNUNG_HOEHE;
        default:
            return 0;
    }
};

/**
 * Ein kurzer Einleitungsabsatz vor einer Tabelle gehoert zu ihr: liefert die
 * Hoehe von Absatz plus Tabellenanfang, sonst `null`.
 */
const einleitungMitTabelle = (s: SatzZustand, bloecke: readonly DokumentBlock[], i: number): number | null => {
    const b = bloecke[i];
    const naechster = bloecke[i + 1];
    if (!b || b.art !== 'absatz' || !naechster || naechster.art !== 'tabelle') return null;
    const zeilen = absatzZeilen(s, b.text).length;
    if (zeilen > EINLEITUNG_MAX_ZEILEN) return null;
    return zeilen * ZEILE.text + ABSTAND.absatz + tabellenAnfangHoehe(s, naechster);
};

/** Was nach einer Ueberschrift mindestens mit auf die Seite muss. */
const folgeHoehe = (s: SatzZustand, a: DokumentAbschnitt, ebene: number): number => {
    if (a.bloecke.length) return einleitungMitTabelle(s, a.bloecke, 0) ?? blockAnfangHoehe(s, a.bloecke[0]);
    const kind = a.unterabschnitte[0];
    if (!kind) return 0;
    return ueberschriftHoehe(s, kind, ebene + 1, true) + folgeHoehe(s, kind, ebene + 1);
};

// ── Zeichnen ──────────────────────────────────────────────────────────────

const zeichneUeberschrift = (s: SatzZustand, a: DokumentAbschnitt, ebene: number) => {
    const h1 = ebene === 1;
    const groesse = h1 ? SCHRIFT.h1 : SCHRIFT.h2;
    const zh = h1 ? ZEILE.h1 : ZEILE.h2;
    const bedarf = ueberschriftHoehe(s, a, ebene, !amSeitenkopf(s)) + folgeHoehe(s, a, ebene);
    if (s.y + bedarf > s.unten && !amSeitenkopf(s)) neueSeite(s);
    if (!amSeitenkopf(s)) s.y += h1 ? ABSTAND.vorH1 : ABSTAND.vorH2;

    s.seiten.set(a.nummer, aktuelleSeite(s.doc));
    const zeilen = ueberschriftZeilen(s, a, ebene);
    farbe(s.doc, FARBE.text);
    schrift(s.doc, 'bold', groesse);
    s.doc.text(a.nummer, GEO.margin, grundlinie(s.y, groesse, zh));
    zeilen.forEach(z => {
        s.doc.text(z, GEO.margin + TAB, grundlinie(s.y, groesse, zh));
        s.y += zh;
    });
    s.y += h1 ? ABSTAND.nachH1 : ABSTAND.nachH2;
};

/**
 * Fliesstext mit Hurenkind- und Schusterjungenregel: bricht ein Absatz um,
 * stehen auf beiden Seiten mindestens zwei Zeilen.
 */
const zeichneAbsatz = (s: SatzZustand, text: string, groesse = SCHRIFT.text, zh = ZEILE.text) => {
    schrift(s.doc, 'normal', groesse);
    const zeilen = umbrechen(s.doc, text, GEO.contentWidth);
    let i = 0;
    while (i < zeilen.length) {
        const rest = zeilen.length - i;
        const frei = Math.floor((s.unten - s.y + 0.01) / zh);
        let n: number;
        if (rest <= frei) n = rest;
        else {
            n = Math.min(frei, rest - 2);
            if (n < 2) {
                if (!amSeitenkopf(s)) { neueSeite(s); continue; }
                n = Math.max(1, frei);
            }
        }
        schrift(s.doc, 'normal', groesse);
        farbe(s.doc, FARBE.text);
        for (let k = 0; k < n; k++) {
            s.doc.text(zeilen[i + k], GEO.margin, grundlinie(s.y, groesse, zh));
            s.y += zh;
        }
        i += n;
        if (i < zeilen.length) neueSeite(s);
    }
    s.y += ABSTAND.absatz;
};

const zeichneAngaben = (s: SatzZustand, zeilen: { bezeichnung: string; wert: string }[]) => {
    if (zeilen.length <= ANGABEN_ZUSAMMEN && s.y + angabenHoehe(s, zeilen) > s.unten && !amSeitenkopf(s)) neueSeite(s);
    zeilen.forEach(z => {
        const { links, rechts } = angabeZeilen(s, z.bezeichnung, z.wert);
        const hoehe = Math.max(links.length, rechts.length) * ZEILE.angabe;
        if (s.y + hoehe > s.unten && !amSeitenkopf(s)) neueSeite(s);
        schrift(s.doc, 'normal', SCHRIFT.text);
        farbe(s.doc, FARBE.grau);
        links.forEach((t, k) => s.doc.text(t, GEO.margin, grundlinie(s.y + k * ZEILE.angabe, SCHRIFT.text, ZEILE.angabe)));
        farbe(s.doc, FARBE.text);
        rechts.forEach((t, k) => s.doc.text(t, GEO.margin + ANGABE_BEZEICHNUNG, grundlinie(s.y + k * ZEILE.angabe, SCHRIFT.text, ZEILE.angabe)));
        s.y += hoehe + ANGABE_ABSTAND;
    });
    s.y += ABSTAND.block - ANGABE_ABSTAND;
};

const zeichneTabelle = (s: SatzZustand, block: Extract<DokumentBlock, { art: 'tabelle' }>) => {
    const breiten = spaltenBreiten(block.spalten);
    const letzte = block.spalten.length - 1;
    const letzteZeile = block.zeilen.length - 1;
    if (s.y + tabellenAnfangHoehe(s, block) > s.unten && !amSeitenkopf(s)) neueSeite(s);

    const polster = (spalte: number) => ({
        top: TABELLE_POLSTER.oben,
        bottom: TABELLE_POLSTER.unten,
        // Erste und letzte Spalte buendig mit dem Satzspiegel — die Linien
        // laufen ueber die volle Breite, der Text steht in der Fluchtlinie
        // des Fliesstexts.
        left: spalte === 0 ? 0 : TABELLE_POLSTER.seite,
        right: spalte === letzte ? 0 : TABELLE_POLSTER.seite,
    });
    const ausrichtung = (spalte: number): 'left' | 'center' | 'right' => {
        const a = block.spalten[spalte] ? block.spalten[spalte].ausrichtung : undefined;
        return a === 'mitte' ? 'center' : a === 'rechts' ? 'right' : 'left';
    };
    const columnStyles: Record<number, { cellWidth: number }> = {};
    block.spalten.forEach((_, i) => { columnStyles[i] = { cellWidth: breiten[i] }; });

    autoTable(s.doc, {
        startY: s.y,
        head: [block.spalten.map(sp => sp.titel)],
        body: block.zeilen.map(z => block.spalten.map((_, i) => String(z[i] ?? ''))),
        theme: 'plain',
        showHead: 'everyPage',
        rowPageBreak: 'avoid',
        tableWidth: GEO.contentWidth,
        margin: { top: s.oben, left: GEO.margin, right: GEO.margin, bottom: s.fussReserve },
        // autoTable legt Folgeseiten selbst an — ohne den Rueckruf blieben
        // sie ohne Briefpapier.
        willDrawPage: s.lh.willDrawPage,
        styles: {
            font: 'helvetica',
            fontSize: SCHRIFT.tabelle,
            textColor: FARBE.text,
            fillColor: false,
            lineColor: FARBE.haar,
            lineWidth: { bottom: LINIE.haar },
            valign: 'top',
            overflow: 'linebreak',
        },
        headStyles: {
            fontStyle: 'bold',
            fillColor: false,
            textColor: FARBE.text,
            lineColor: FARBE.linie,
            lineWidth: { top: LINIE.kraeftig, bottom: LINIE.kopf },
        },
        columnStyles,
        didParseCell: (data) => {
            // Polster und Ausrichtung hier statt in `columnStyles`: die gelten
            // bei autoTable nur fuer den Rumpf, der Kopf stuende sonst
            // eingerueckt und anders ausgerichtet als seine Spalte.
            const p = polster(data.column.index);
            data.cell.styles.cellPadding = p;
            data.cell.styles.halign = ausrichtung(data.column.index);
            // Dieselbe Toleranz wie autoTable beim Umbruch (1 pt): was dort
            // ungetrennt passt, bekommt hier keinen Trennstrich.
            const verfuegbar = (breiten[data.column.index] || 20) - p.left - p.right + PT;
            schrift(s.doc, data.cell.styles.fontStyle === 'bold' ? 'bold' : 'normal', SCHRIFT.tabelle);
            const messen = (t: string) => s.doc.getTextWidth(t);
            data.cell.text = (data.cell.text || [])
                .map(z => trenneUeberlangeWoerter(z, verfuegbar, messen))
                .flatMap(z => z.split('\n'));
            if (data.section === 'body' && data.row.index === letzteZeile) {
                data.cell.styles.lineWidth = { bottom: LINIE.kraeftig };
                data.cell.styles.lineColor = FARBE.linie;
            }
        },
    });
    const ende = (s.doc as unknown as { lastAutoTable?: { finalY?: number } }).lastAutoTable;
    s.y = (ende && typeof ende.finalY === 'number' ? ende.finalY : s.y) + ABSTAND.block + 1;
    // autoTable stellt Schrift und Farben nicht zurueck.
    farbe(s.doc, FARBE.text);
};

const zeichneZeichnung = (s: SatzZustand, felder: { beschriftung: string; name?: string }[]) => {
    if (s.y + ZEICHNUNG_HOEHE > s.unten && !amSeitenkopf(s)) neueSeite(s);
    const breite = 75;
    const abstand = GEO.contentWidth - 2 * breite;
    const linieY = s.y + 20;
    felder.slice(0, 2).forEach((f, i) => {
        const x = GEO.margin + i * (breite + abstand);
        linienfarbe(s.doc, FARBE.linie);
        s.doc.setLineWidth(LINIE.kopf);
        s.doc.line(x, linieY, x + breite, linieY);
        let ty = linieY + 4.2;
        if (f.name) {
            schrift(s.doc, 'normal', SCHRIFT.text - 0.5);
            farbe(s.doc, FARBE.text);
            s.doc.text(f.name, x, ty);
            ty += 4.4;
        }
        schrift(s.doc, 'normal', SCHRIFT.klein);
        farbe(s.doc, FARBE.grau);
        s.doc.text(f.beschriftung, x, ty);
    });
    farbe(s.doc, FARBE.text);
    s.y = linieY + 14;
};

const zeichneBlock = (s: SatzZustand, block: DokumentBlock) => {
    switch (block.art) {
        case 'absatz': zeichneAbsatz(s, block.text); break;
        case 'angaben': zeichneAngaben(s, block.zeilen); break;
        case 'tabelle': zeichneTabelle(s, block); break;
        case 'zeichnung': zeichneZeichnung(s, block.felder); break;
        default: break;
    }
};

const zeichneAbschnitt = (s: SatzZustand, a: DokumentAbschnitt, ebene: number) => {
    zeichneUeberschrift(s, a, ebene);
    a.bloecke.forEach((b, i) => {
        const gebunden = einleitungMitTabelle(s, a.bloecke, i);
        if (gebunden !== null && s.y + gebunden > s.unten && !amSeitenkopf(s)) neueSeite(s);
        zeichneBlock(s, b);
    });
    a.unterabschnitte.forEach(u => zeichneAbschnitt(s, u, ebene + 1));
};

// ── Titelseite ────────────────────────────────────────────────────────────

const zeichneTitelseite = (s: SatzZustand, modell: DokumentModell, kopf: { addressFieldY: number; rightColBottom: number }) => {
    const { doc } = s;
    s.y = Math.max(kopf.addressFieldY + 24, kopf.rightColBottom + 16, 78);

    farbe(doc, FARBE.text);
    schrift(doc, 'bold', SCHRIFT.titel);
    umbrechen(doc, modell.titel, GEO.contentWidth).forEach(z => {
        doc.text(z, GEO.margin, grundlinie(s.y, SCHRIFT.titel, 8.6));
        s.y += 8.6;
    });
    s.y += 1.5;
    schrift(doc, 'normal', SCHRIFT.untertitel);
    umbrechen(doc, modell.untertitel, GEO.contentWidth).forEach(z => {
        doc.text(z, GEO.margin, grundlinie(s.y, SCHRIFT.untertitel, 5.6));
        s.y += 5.6;
    });

    s.y += 9;
    linienfarbe(doc, FARBE.linie);
    doc.setLineWidth(LINIE.kraeftig);
    doc.line(GEO.margin, s.y, GEO.rightX, s.y);
    s.y += 4;

    modell.titelei.forEach((z, i) => {
        schrift(doc, 'normal', SCHRIFT.text);
        const links = umbrechen(doc, z.bezeichnung, ANGABE_BEZEICHNUNG - 3);
        // Das Bauvorhaben ist die Hauptsache der Titelei.
        schrift(doc, i === 0 ? 'bold' : 'normal', SCHRIFT.text);
        const rechts = umbrechen(doc, z.wert, GEO.contentWidth - ANGABE_BEZEICHNUNG);
        const zeilen = Math.max(links.length, rechts.length);
        schrift(doc, 'normal', SCHRIFT.text);
        farbe(doc, FARBE.grau);
        links.forEach((t, k) => doc.text(t, GEO.margin, grundlinie(s.y + k * ZEILE.angabe, SCHRIFT.text, ZEILE.angabe)));
        schrift(doc, i === 0 ? 'bold' : 'normal', SCHRIFT.text);
        farbe(doc, FARBE.text);
        rechts.forEach((t, k) => doc.text(t, GEO.margin + ANGABE_BEZEICHNUNG, grundlinie(s.y + k * ZEILE.angabe, SCHRIFT.text, ZEILE.angabe)));
        s.y += zeilen * ZEILE.angabe + (ZEILE.titelei - ZEILE.angabe);
    });

    s.y += 2;
    linienfarbe(doc, FARBE.linie);
    doc.setLineWidth(LINIE.haar);
    doc.line(GEO.margin, s.y, GEO.rightX, s.y);
    s.y += 10;

    if (modell.vorbemerkung.length) {
        if (s.y + 30 > s.unten) neueSeite(s);
        schrift(doc, 'bold', SCHRIFT.h2);
        farbe(doc, FARBE.text);
        doc.text('Vorbemerkung', GEO.margin, grundlinie(s.y, SCHRIFT.h2, ZEILE.h2));
        s.y += ZEILE.h2 + ABSTAND.nachH2;
        modell.vorbemerkung.forEach(t => zeichneAbsatz(s, t));
    }
};

// ── Inhaltsverzeichnis ────────────────────────────────────────────────────

interface VerzeichnisZeile {
    nummer: string;
    zeilen: string[];
    ebene: number;
    hoehe: number;
}

const VERZEICHNIS_KOPF = 14;

const verzeichnisZeilen = (s: SatzZustand, modell: DokumentModell): VerzeichnisZeile[] =>
    gliederung(modell).map((e, i) => {
        const erste = e.ebene === 1;
        const textX = erste ? TAB : 2 * TAB;
        schrift(s.doc, erste ? 'bold' : 'normal', SCHRIFT.text);
        const zeilen = umbrechen(s.doc, e.titel, GEO.contentWidth - textX - 16);
        const zh = erste ? ZEILE.verzeichnis1 : ZEILE.verzeichnis2;
        return {
            nummer: e.nummer,
            zeilen,
            ebene: e.ebene,
            hoehe: zeilen.length * zh + (erste && i > 0 ? 2 : 0),
        };
    });

/** Verteilt die Verzeichniszeilen auf Seiten — dieselbe Rechnung fuer das
 *  Freihalten und das spaetere Schreiben. */
const verteileVerzeichnis = (s: SatzZustand, zeilen: VerzeichnisZeile[]): VerzeichnisZeile[][] => {
    const seiten: VerzeichnisZeile[][] = [[]];
    let y = s.oben + VERZEICHNIS_KOPF;
    zeilen.forEach(z => {
        if (y + z.hoehe > s.unten && seiten[seiten.length - 1].length) {
            seiten.push([]);
            y = s.oben;
        }
        seiten[seiten.length - 1].push(z);
        y += z.hoehe;
    });
    return seiten;
};

const zeichneVerzeichnis = (s: SatzZustand, seiten: VerzeichnisZeile[][], ersteSeite: number) => {
    const { doc } = s;
    seiten.forEach((liste, si) => {
        doc.setPage(ersteSeite + si);
        let y = s.oben;
        if (si === 0) {
            schrift(doc, 'bold', SCHRIFT.verzeichnisTitel);
            farbe(doc, FARBE.text);
            doc.text('Inhalt', GEO.margin, grundlinie(y, SCHRIFT.verzeichnisTitel, ZEILE.h1));
            y += VERZEICHNIS_KOPF;
        }
        liste.forEach((z, zi) => {
            const erste = z.ebene === 1;
            const zh = erste ? ZEILE.verzeichnis1 : ZEILE.verzeichnis2;
            if (erste && zi > 0) y += 2;
            const nummerX = GEO.margin + (erste ? 0 : TAB);
            const textX = GEO.margin + (erste ? TAB : 2 * TAB);
            const stil = erste ? 'bold' : 'normal';
            farbe(doc, FARBE.text);
            schrift(doc, stil, SCHRIFT.text);
            doc.text(z.nummer, nummerX, grundlinie(y, SCHRIFT.text, zh));
            z.zeilen.forEach((t, k) => {
                doc.text(t, textX, grundlinie(y + k * zh, SCHRIFT.text, zh));
            });
            const letzteY = y + (z.zeilen.length - 1) * zh;
            const seite = String(s.seiten.get(z.nummer) ?? '');
            doc.text(seite, GEO.rightX, grundlinie(letzteY, SCHRIFT.text, zh), { align: 'right' });

            // Fuehrungspunkte zwischen Titel und Seitenzahl.
            const textEnde = textX + doc.getTextWidth(z.zeilen[z.zeilen.length - 1] || '');
            const zahlAnfang = GEO.rightX - doc.getTextWidth(seite);
            schrift(doc, 'normal', SCHRIFT.text);
            const schritt = doc.getTextWidth('. ');
            const platz = zahlAnfang - textEnde - 3;
            const anzahl = Math.floor(platz / schritt);
            if (anzahl > 2) {
                farbe(doc, FARBE.grau);
                const punkte = '. '.repeat(anzahl).trimEnd();
                doc.text(punkte, zahlAnfang - 2, grundlinie(letzteY, SCHRIFT.text, zh), { align: 'right' });
            }
            y += z.zeilen.length * zh;
        });
    });
    farbe(doc, FARBE.text);
};

// ── Kopf- und Fusszeilen ──────────────────────────────────────────────────

const kuerzeAufBreite = (doc: jsPDF, text: string, breite: number): string => {
    if (doc.getTextWidth(text) <= breite) return text;
    let t = text;
    while (t.length > 1 && doc.getTextWidth(`${t}…`) > breite) t = t.slice(0, -1);
    return `${t.trimEnd()}…`;
};

const zeichneKopfUndFuss = (s: SatzZustand, modell: DokumentModell) => {
    const { doc, lh, company } = s;
    const { pageHeight, margin, rightX, pageWidth } = GEO;
    const gesamt = doc.getNumberOfPages();
    const kopfY = lh.hasStationery ? lh.topFollow + 2 : 13;

    // Spalten-Fusszeile wie `drawFooters` (Rechnung, Angebot, Klaerungsliste).
    const fs = company.footerFontSize ?? 7;
    const spalten = (company.footerColumns ?? [])
        .map(col => col.map(l => (l || '').trim()).filter(Boolean))
        .filter(col => col.length > 0);
    let umbrochen: string[][] = [];
    let spaltenX: number[] = [];
    let spaltenZeile = 3.3;
    if (spalten.length && !lh.hasStationery) {
        schrift(doc, 'normal', fs);
        const luecke = 4;
        const verfuegbar = rightX - margin - luecke * (spalten.length - 1);
        const natuerlich = spalten.map(col => Math.max(...col.map(z => doc.getTextWidth(z))));
        const breiten = footerColumnWidths(natuerlich, verfuegbar, company.footerFlexColumn);
        let x = margin;
        spaltenX = breiten.map(b => { const a = x; x += b + luecke; return a; });
        umbrochen = spalten.map((col, i) => col.flatMap(z => doc.splitTextToSize(z, breiten[i] + 0.1) as string[]));
        const maxZeilen = Math.max(...umbrochen.map(c => c.length));
        if (maxZeilen > 1) spaltenZeile = Math.min(3.3, 13 / (maxZeilen - 1));
    }
    const fussText = (company.footerText ?? company.name ?? '').trim().replace(/\s*\n\s*/g, ' ');

    for (let i = 1; i <= gesamt; i++) {
        doc.setPage(i);

        if (i >= 2) {
            schrift(doc, 'normal', SCHRIFT.klein);
            farbe(doc, FARBE.grau);
            const rechts = modell.titel;
            const rechtsBreite = doc.getTextWidth(rechts);
            doc.text(kuerzeAufBreite(doc, modell.bauvorhaben, GEO.contentWidth - rechtsBreite - 8), margin, kopfY);
            doc.text(rechts, rightX, kopfY, { align: 'right' });
            linienfarbe(doc, FARBE.haar);
            doc.setLineWidth(LINIE.haar);
            doc.line(margin, kopfY + 2, rightX, kopfY + 2);
        }

        const stand = `Stand: ${modell.standDatum}`;
        const seite = `Seite ${i} von ${gesamt}`;
        schrift(doc, 'normal', SCHRIFT.klein);
        farbe(doc, FARBE.grau);
        // Die interne Fassung sagt es auf jeder Seite — mittig zwischen Stand
        // und Seitenzahl, in Textfarbe, damit es sich vom grauen Stand abhebt.
        const intern = (y: number) => {
            if (!modell.mitInternerBewertung) return;
            const frei = GEO.contentWidth - doc.getTextWidth(stand) - doc.getTextWidth(seite) - 8;
            farbe(doc, FARBE.text);
            doc.text(kuerzeAufBreite(doc, FUSS_INTERN, frei), pageWidth / 2, y, { align: 'center' });
            farbe(doc, FARBE.grau);
        };

        if (lh.hasStationery) {
            const y = pageHeight - Math.max(lh.botMargin - 5, 4);
            doc.text(stand, margin, y);
            doc.text(seite, rightX, y, { align: 'right' });
            intern(y);
            continue;
        }

        if (umbrochen.length) {
            doc.text(stand, margin, pageHeight - 23);
            doc.text(seite, rightX, pageHeight - 23, { align: 'right' });
            intern(pageHeight - 23);
            linienfarbe(doc, FARBE.haar);
            doc.setLineWidth(LINIE.haar);
            doc.line(margin, pageHeight - 21, rightX, pageHeight - 21);
            schrift(doc, 'normal', fs);
            farbe(doc, FARBE.text);
            umbrochen.forEach((col, c) => col.forEach((z, l) => doc.text(z, spaltenX[c], pageHeight - 18 + l * spaltenZeile)));
            continue;
        }

        doc.text(stand, margin, pageHeight - 20);
        doc.text(seite, rightX, pageHeight - 20, { align: 'right' });
        intern(pageHeight - 20);
        linienfarbe(doc, FARBE.haar);
        doc.setLineWidth(LINIE.haar);
        doc.line(margin, pageHeight - 15, rightX, pageHeight - 15);
        if (fussText) {
            schrift(doc, 'normal', fs);
            farbe(doc, FARBE.text);
            doc.text(fussText, pageWidth / 2, pageHeight - 10, { align: 'center' });
        }
    }
};

// ═══════════════════════════════════════════════════════════════════════════
// EINSTIEG
// ═══════════════════════════════════════════════════════════════════════════

const setze = (doc: jsPDF, modell: DokumentModell, company: CompanyInfo): void => {
    // Netz fuer Freitext (Rahmenzeichen, Pfeile) — am Dokument, damit keine
    // Ausgabestelle vergessen werden kann.
    sichereTextausgabe(doc);
    // autoTable setzt vor jeder Zelle die AKTUELLE Fuellfarbe neu. Die
    // Vorgabe von jsPDF ist Schwarz; Weiss stellt sicher, dass nirgends eine
    // Flaeche entstehen kann.
    doc.setFillColor(FARBE.papier[0], FARBE.papier[1], FARBE.papier[2]);
    doc.setProperties({
        title: `${modell.titel} · ${modell.bauvorhaben}`,
        subject: modell.untertitel,
        ...(company.name ? { author: company.name, creator: company.name } : {}),
    });

    const lh = createLetterhead(doc, company, GEO);
    lh.stampCurrentBg();
    const kopf = drawPage1Header(doc, company, lh, GEO);
    const fussReserve = lh.hasStationery ? lh.botMargin : FOOTER_RESERVE;

    const s: SatzZustand = {
        doc,
        lh,
        company,
        y: 0,
        oben: lh.hasStationery ? lh.topFollow + 10 : lh.contentTopFollowing,
        unten: GEO.pageHeight - fussReserve,
        fussReserve,
        seiten: new Map(),
    };

    zeichneTitelseite(s, modell, kopf);

    // Verzeichnisseiten freihalten.
    const verzeichnis = verteileVerzeichnis(s, verzeichnisZeilen(s, modell));
    const ersteVerzeichnisSeite = doc.getNumberOfPages() + 1;
    verzeichnis.forEach(() => s.lh.addPage());

    // Inhalt auf eigener Seite.
    neueSeite(s);
    modell.abschnitte.forEach(a => zeichneAbschnitt(s, a, 1));

    zeichneVerzeichnis(s, verzeichnis, ersteVerzeichnisSeite);
    zeichneKopfUndFuss(s, modell);
};

export interface PlanungsdokumentationOptionen {
    /** Nur fuer Tests: ein vorbereitetes jsPDF-Objekt (Muster `__docFactory`). */
    __docFactory?: () => jsPDF;
}

/**
 * Erzeugt die Dokumentation der Planungsphase.
 *
 * Gibt das Dokument zurueck, keine Bytes: der Aufrufer speichert
 * (`speicherePdf`), legt ab (`pdfAlsBlob`) oder zeigt an. Fehler aus jsPDF
 * oder der Briefkopfaufbereitung kommen als `{ status: 'fehler' }` zurueck
 * und werfen nicht in die Komponente.
 */
export const erzeugePlanungsdokumentation = (
    modell: DokumentModell,
    pdf: PdfKontext,
    optionen: PlanungsdokumentationOptionen = {},
): Anschluss<jsPDF> => {
    try {
        const company = buildCompanyForPdf(
            pdf.company as never,
            pdf.quoteConfig as never,
            pdf.invoiceConfig as never,
        );
        const doc = optionen.__docFactory
            ? optionen.__docFactory()
            : new jsPDF({ format: 'a4', unit: 'mm', orientation: 'portrait', compress: true });
        setze(doc, modell, company || {});
        return { status: 'ok', wert: doc };
    } catch (e) {
        return {
            status: 'fehler',
            fehler: `Die Dokumentation konnte nicht erzeugt werden: ${e instanceof Error ? e.message : String(e)}`,
        };
    }
};
